import { LoadingPage } from "./../config/index";
import router from "@/router";
import authStore from "@/store/AuthStore";
import { Dialog } from "vant";
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";

export interface ApiResult<T = any> {
  code: number;
  message: string;
  data: T;
}

axios.defaults.baseURL = import.meta.env.VITE_BASE_URL as string;
axios.defaults.timeout = 30e3;
axios.defaults.headers.common = {
  "Content-Type": "application/json",
  "Cache-Control": "no-cache",
  Expires: "0",
  Pragma: "no-cache",
};

// default timeout is 0 (no timeout)
const service = axios.create();

// request interceptor
service.interceptors.request.use(
  async (config: AxiosRequestConfig) => {
    const authToken = authStore.getToken();
    if (authToken) {
      config.headers!["Authorization"] = `Bearer ${authToken}`;
    }
    return config;
  },
  (err) => {
    console.error(err);
    return Promise.reject(err);
  }
);

// response interceptor
service.interceptors.response.use(
  (response) => {
    const result = response.data as ApiResult<any>;
    if (
      result.code !== 20000 ||
      (result.message !== "" && result.message?.toLowerCase() !== "success")
    ) {
      console.error(result.message);
      // Dialog.alert({
      //   message: result.message,
      // }).then();
      return Promise.reject(new Error(result.message));
    } else {
      return response;
    }
  },
  (error: AxiosError) => {
    const resp = error.response as Record<string, any>;
    if (resp && resp.status) {
      switch (resp.status) {
        case 401:
        case 403:
          authStore.removeToken();
          window.location.replace(LoadingPage);
          break;
        default:
          Dialog.alert({
            message: resp?.data.message || resp.statusText,
          }).then();
      }
    }
    return Promise.reject(
      new Error((resp?.data as ApiResult).message ?? "Unknown error")
    );
  }
);

const { CancelToken, isCancel } = axios;

async function post<T>(tUrl: string, params?: Record<string, any>) {
  const source = CancelToken.source();

  if (params === undefined) {
    params = {};
  }

  try {
    let resp = await service.post<ApiResult<T>>(tUrl, params, {
      cancelToken: source.token,
    });
    return parseResponse<T>(resp);
  } catch (err: any) {
    if (isCancel(err)) {
      console.log("Request canceled", err.message);
    }
    // message.error(err.message);
    // alert(err.message);
    throw err;
  }
}

async function put<T>(tUrl: string, params?: Record<string, any>) {
  const source = CancelToken.source();

  if (params === undefined) {
    params = {};
  }

  try {
    let resp = await service.put<ApiResult<T>>(tUrl, params, {
      cancelToken: source.token,
    });
    return parseResponse<T>(resp);
  } catch (err: any) {
    if (isCancel(err)) {
      console.log("Request canceled", err.message);
    }
    // message.error(err.message);
    // alert(err.message);
    throw err;
  }
}

async function get<T>(tUrl: string, params?: Record<string, any>) {
  const source = CancelToken.source();

  if (params === undefined) {
    params = {};
  }
  if (params) {
    params.__ = new Date().getTime();
  } else {
    params = {
      __: new Date().getTime(),
    };
  }

  try {
    let resp = await service.get<ApiResult<T>>(tUrl, {
      params,
      cancelToken: source.token,
    });
    return parseResponse<T>(resp);
  } catch (err: any) {
    if (isCancel(err)) {
      console.log("Request canceled", err.message);
    }
    // alert(err.message);
    throw err;
  }
}

async function del<T>(tUrl: string, params?: Record<string, any>) {
  const source = CancelToken.source();

  if (params === undefined) {
    params = {};
  }

  try {
    let resp = await service.delete<ApiResult<T>>(tUrl, {
      params,
      cancelToken: source.token,
    });
    return parseResponse<T>(resp);
  } catch (err: any) {
    if (isCancel(err)) {
      console.log("Request canceled", err.message);
    }
    // alert(err.message);
    throw err;
  }
}

async function download(tUrl: string, params?: Record<string, any>) {
  const service = axios.create();
  const source = CancelToken.source();
  try {
    let resp = await service.post(tUrl, params, {
      cancelToken: source.token,
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([resp.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      resp.headers["content-disposition"].split("filename=")[1]
    );
    document.body.appendChild(link);
    link.click();
    return resp;
  } catch (err: any) {
    if (isCancel(err)) {
      console.log("Request canceled", err.message);
    }
    // alert(err.message);
    throw err;
  }
}

function parseResponse<T>(response: AxiosResponse<ApiResult<T>>): T {
  let resp: ApiResult<T> = {
    ...response.data,
  };
  return resp.data as T;
}

// a fetching similator
function delay(t: number, v?: any): Promise<any> {
  return new Promise(function (resolve) {
    setTimeout(resolve.bind(null, v), t);
  });
}

export { get, post, put, del, del as delete, download };
